import { CalendarEvent, DisplayEvent } from '../types';
import { useLocale } from '@/common/composables/useLocale';

import { logger } from '@/common/services/config';
export const getEventClass = (event: CalendarEvent | DisplayEvent) => {
  // If it's a DisplayEvent, use the originalEvent for type checking
  const calendarEvent = 'originalEvent' in event ? event.originalEvent : event;

  return {
    'event-appointment':
      calendarEvent.type === 'PersonalEvent' && 'isMine' in calendarEvent && !calendarEvent.isMine,
    'event-personal': 'isMine' in calendarEvent && calendarEvent.isMine,
    'event-course': calendarEvent.type === 'Activity',
    'event-session': calendarEvent.type === 'Session',
    'event-exam': calendarEvent.type === 'Exam',
  };
};

export const getEventTypeLabel = (type: string) => {
  const { t } = useLocale();
  const labels = {
    Activity: t('agenda.eventType.course'),
    Exam: t('agenda.eventType.exam'),
    Session: t('agenda.eventType.session'),
    PersonalEvent: t('agenda.eventType.appointment'),
  };
  return labels[type as keyof typeof labels] || type;
};

export const isEventMultiDay = (startDate: string, endDate: string): boolean => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Compare dates without time (only day, month, year)
  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  return startDay.getTime() !== endDay.getTime();
};

export const createDisplayEvents = (events: CalendarEvent[]): DisplayEvent[] => {
  const displayEvents: DisplayEvent[] = [];

  events.forEach((event) => {
    const isMultiDay = isEventMultiDay(event.start, event.end);

    if (!isMultiDay) {
      // Single day event - create display event that references original
      displayEvents.push({
        ...event,
        displayId: event.id,
        originalEvent: event,
      });
    } else {
      // Multi-day event - split into daily display segments
      const startDate = new Date(event.start);
      const endDate = new Date(event.end);
      const currentDate = new Date(startDate);
      let dayIndex = 0;

      while (currentDate <= endDate) {
        const isFirstDay = dayIndex === 0;
        const isLastDay = currentDate.toDateString() === endDate.toDateString();

        const dayStart = isFirstDay
          ? new Date(startDate)
          : new Date(
              currentDate.getFullYear(),
              currentDate.getMonth(),
              currentDate.getDate(),
              0,
              0,
              0
            );

        const dayEnd = isLastDay
          ? new Date(endDate)
          : new Date(
              currentDate.getFullYear(),
              currentDate.getMonth(),
              currentDate.getDate(),
              23,
              59,
              59
            );

        displayEvents.push({
          ...event,
          id: event.id, // Keep original ID for API operations
          displayId: `${event.id}_display_${dayIndex}`, // Unique display ID
          start: dayStart.toISOString(),
          end: dayEnd.toISOString(),
          label: isFirstDay ? event.label : `${event.label} (cont.)`,
          originalEvent: event, // Reference to original event
          isDisplaySegment: true,
          dayIndex: dayIndex,
          isFirstDay: isFirstDay,
          isLastDay: isLastDay,
        });

        // Move to next day
        currentDate.setDate(currentDate.getDate() + 1);
        dayIndex++;

        // Safety check to prevent infinite loops
        if (dayIndex > 30) {
          logger.warn('Event spans more than 30 days, breaking loop:', event);
          break;
        }
      }
    }
  });

  return displayEvents;
};

export const isMultiDaySegment = (event: DisplayEvent | CalendarEvent): event is DisplayEvent => {
  return 'isDisplaySegment' in event && event.isDisplaySegment === true;
};

export const getMultiDayClasses = (event: DisplayEvent | CalendarEvent) => {
  if (!isMultiDaySegment(event)) return {};

  return {
    'multi-day': true,
    'first-day': event.isFirstDay,
    'last-day': event.isLastDay,
    'middle-day': !event.isFirstDay && !event.isLastDay,
  };
};

export const getEventDurationInDays = (event: CalendarEvent): number => {
  const start = new Date(event.start);
  const end = new Date(event.end);

  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  const diffTime = endDay.getTime() - startDay.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays + 1; // Include both start and end days
};
